import { Trie } from './trie';

export class KeyedTrie<T extends { name: string } = { name: string }> {
  private tries: Map<string, Trie>;
  private items: Map<string, Map<string, T>>;

  constructor() {
    this.tries = new Map();
    this.items = new Map();
  }

  private getOrCreateTrie(key: string): Trie {
    let trie = this.tries.get(key);
    if (!trie) {
      trie = new Trie();
      this.tries.set(key, trie);
    }
    return trie;
  }

  private getOrCreateItemMap(key: string): Map<string, T> {
    let map = this.items.get(key);
    if (!map) {
      map = new Map();
      this.items.set(key, map);
    }
    return map;
  }

  /**
   * Insert an object (with a `name` property) that belongs to a specific key.
   * Example: keyedTrie.insert("key1", { name: "apple", type: "fruit" })
   */
  insert(key: string, item: T): void {
    if (!item) return;
    const trie = this.getOrCreateTrie(key);
    const map = this.getOrCreateItemMap(key);
    map.set(item.name, item);
    trie.insert(item.name);
  }

  /**
   * Construct a trie for a specific key with a list of objects.
   * Searching/autocomplete uses the objects' `name` property.
   * @param key
   * @param items
   */
  construct(key: string, items: T[]): void {
    const trie = this.getOrCreateTrie(key);
    const map = this.getOrCreateItemMap(key);
    map.clear();
    const names: string[] = [];
    for (const item of items) {
      if (!item) continue;
      map.set(item.name, item);
      names.push(item.name);
    }
    trie.construct(names);
  }

  /**
   * Check if the given key contains an object with the specified name.
   */
  contains(key: string, name: string): boolean {
    const trie = this.tries.get(key);
    if (!trie) return false;
    return trie.contains(name);
  }

  /**
   * Autocomplete within a specific key.
   * Returns the full objects whose `name` matches the prefix.
   * Example: keyedTrie.autocomplete("key1", "ap") -> [{ name: "apple", ... }, ...]
   */
  autocomplete(key: string, prefix: string, limit = Infinity): T[] {
    const trie = this.tries.get(key);
    if (!trie) return [];
    const names = trie.autocomplete(prefix, limit);
    const map = this.items.get(key);
    if (!map) return [];
    return (names.length > 0 ? names : Array.from(map.keys())).map((n) => map.get(n)).filter(Boolean) as T[];
  }

  /**
   * Get all keys you currently have tries for.
   */
  keys(): string[] {
    return Array.from(this.tries.keys());
  }
}
